import { Component, HostListener, Inject, OnInit, Optional } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { LayoutService } from '@app/core/services/layout.service';
import { PrintService } from '@app/core/services/print.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { P2ReportDefinitionService, P2ReportDefinitionsSettings } from '@app/services/p2-report-definition.service';
import { Item } from '@app/core/components/listbox/listbox.component';

@Component({
    selector: 'tt-p2-report-defs',
    templateUrl: './p2-report-defs.component.html',
    styleUrls: ['./p2-report-defs.component.css'],
    standalone: false
})
export class P2ReportDefsComponent implements OnInit {
    public readonly tabletSize: MediaQueryList;

    public setting?: P2ReportDefinitionsSettings;

    public selectedBgReportDefinitions: string = 'REPORT';

    public updatingChanges = false;

    public reports: Item[] = [];

    public printers: Item[] = [];

    public clients: Item[] = [];

    public gridReports?: GridOptions;

    public printersGrid?: GridOptions;

    public printerLocationGrid?: GridOptions;

    private navigator?: TTViewNavigator;

    constructor(
        private definitions: P2ReportDefinitionService,
        private print: PrintService,
        layout: LayoutService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) navigator?: TTViewNavigator,
    ) {
        this.tabletSize = layout.getMediaQueries().tablet;
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async updateChanges(): Promise<void> {
        if (this.updatingChanges) return;

        this.updatingChanges = true;

        try {
            const [reports, printers, clients] = await Promise.all([
                this.print.printService.getReports(),
                this.print.printService.getPrinters(),
                this.print.printService.getClients()
            ]);

            await Promise.all([
                this.definitions.updateReports(reports),
                this.definitions.updatePrinters(printers),
                this.definitions.updateClients(clients)
            ]);

            await Promise.all([this.loadReports(), this.loadGridReports(), this.loadClients()]);
        } finally {
            this.updatingChanges = false;
        }
    }

    public async ngOnInit(): Promise<void> {
        this.setting = await this.definitions.getSettings();
        this.setting.filtervalue_reports ??= '';

        await this.loadClients();
        await this.onWindowResize();
    }

    @HostListener('window:resize')
    public async onWindowResize(): Promise<void> {
        if (this.tabletSize.matches) {
            this.printersGrid = this.createPrintersGrid();
            this.printerLocationGrid = this.printerLocationGrid ?? this.createPrinterLocationGrid();

            await this.loadGridReports();
        } else {
            await Promise.all([this.loadReports(), this.loadPrinters()]);
        }
    }

    private async loadReports(): Promise<void> {
        this.reports = await this.definitions.listReports();
        this.updatingChanges = false;
    }

    private async loadPrinters(): Promise<void> {
        this.printers = await this.definitions.listPrinters();
        this.updatingChanges = false;
    }

    private async loadClients(): Promise<void> {
        this.clients = await this.definitions.listClients();
        this.updatingChanges = false;
    }

    private async loadGridReports(): Promise<void> {
        if (!this.gridReports) {
            this.gridReports = new Grid()
                .setRememberId('p2reportdef_list_grid')
                .setLoadDataFunction({
                    method: 3735,
                    parameters: {}
                })
                .setKendoConfig({ height: '100%', sortable: true, aggregate: true, pager: false, selectable: false, filterable: false })
                .setCustomToolbarButtons([{
                    name: 'p2reportdefs_new',
                    text: 'p2reportdefs_new',
                    func: () => this.navigator?.go({ to: 'p2reportdef', parms: { p2_reportdef_keyno: '0' } }),
                    icon: 'fa-plus',
                    cssClass: 'btn btn-primary im-grid-btn-xs-r',
                    translate: true
                }]);
        } else {
            this.gridReports.gridfunc?.read();
        }

        this.updatingChanges = false;
    }

    private createPrintersGrid(): GridOptions {
        return new Grid()
            .setRememberId('w_p2reportdefs.printersgrid')
            .setLoadDataFunction({
                method: 3594,
                parameters: {}
            })
            .setSaveDataFunction({ method: null, parameters: {}, autoSave: true })
            .setKendoConfig({ height: '100%', sortable: true, aggregate: true, pager: false, selectable: false, filterable: false });
    }

    private createPrinterLocationGrid(): GridOptions {
        return new Grid()
            .setRememberId('w_p2reportdefs.printerlocationgrid')
            .setLoadDataFunction({
                method: 3593,
                parameters: {}
            })
            .setAddRowFunction({ method: null, parameters: {}, autoSave: true })
            .setSaveDataFunction({ method: null, parameters: {}, autoSave: true })
            .setRemoveRowFunction({ method: null, parameters: {}, autoSave: true })
            .setKendoConfig({
                height: '100%',
                sortable: true,
                aggregate: true,
                pager: false,
                selectable: false,
                filterable: false
            })
            .setSpecialFunc({ buttons: [{
                name: 'customDelete',
                text: '',
                icon: 'far-trash-alt',
                func: async (row) => {
                    await this.printerLocationGrid?.gridfunc?.removeRow(row);
                    await this.printerLocationGrid?.gridfunc?.saveChanges(false);
                    await this.printersGrid?.gridfunc?.read();
                },
            }] });
    }
}
